import { api } from './client';

/**
 * The customer's own picture, kept where it survives a reinstall.
 *
 * ---------------------------------------------------------------------------
 * IT USED TO LIVE ON ONE HANDSET AND NOWHERE ELSE
 * ---------------------------------------------------------------------------
 * The edit sheet has had a photo since it was written, and it never left the
 * phone. `PATCH /auth/me` carries `name` and `email` and nothing else; the
 * chosen picture went into local storage as a `file:///data/user/0/...` path,
 * which is meaningful only inside that one install. So somebody picked a
 * photo, saw it, and lost it the next time they reinstalled or changed phone -
 * with nothing anywhere to restore it from.
 *
 * ---------------------------------------------------------------------------
 * THREE STEPS, AND THE BYTES SKIP OUR SERVER
 * ---------------------------------------------------------------------------
 * Ask for a presigned URL, PUT the file straight at S3, then record the key.
 * The image never passes through the backend - a multi-megabyte upload on
 * Indian mobile data has no business occupying a request thread, which is why
 * every other file on this platform is uploaded the same way.
 *
 * Nothing is recorded until the third step, so an upload that fails halfway
 * leaves the account exactly as it was rather than pointing at a half-written
 * object.
 */

type UploadTicket = {
  /** Where the object will live. Send this back in step three. */
  key: string;
  /** PUT the bytes here, with the same Content-Type. */
  uploadUrl: string;
  expiresIn: number;
};

type ViewUrl = {
  /** Short-lived signed GET. Do not persist it - it expires. */
  viewUrl: string;
  expiresIn: number;
};

/** What a phone camera actually produces, matching what the server accepts. */
const CONTENT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
};

/**
 * The picker gives a filename or a type, and sometimes neither.
 *
 * JPEG is the fallback because that is what the library returns after the
 * downscale the picker is asked for - an unrecognised extension is far more
 * likely to be a JPEG the OS labelled oddly than a format the server refuses.
 */
export function contentTypeFor(source?: {
  type?: string | null;
  fileName?: string | null;
}): string {
  const declared = source?.type?.toLowerCase();
  if (declared && Object.values(CONTENT_TYPES).includes(declared)) {
    return declared;
  }

  const ext = source?.fileName?.split('.').pop()?.toLowerCase();
  return (ext && CONTENT_TYPES[ext]) || 'image/jpeg';
}

const requestUploadUrl = (contentType: string) =>
  api.post<UploadTicket>('/customers/me/photo/upload-url', { contentType });

const recordPhoto = (key: string) =>
  api.put<{ key: string }>('/customers/me/photo', { key });

/**
 * A signed URL for the picture on the account, or null when there is none.
 *
 * A 404 is the ordinary answer for most customers and not a failure worth
 * reporting: it means "no picture set", and the caller draws initials.
 */
export async function fetchPhotoUrl(
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const { viewUrl } = await api.get<ViewUrl>('/customers/me/photo', {
      signal,
    });
    return viewUrl;
  } catch {
    return null;
  }
}

/**
 * Uploads a picked image and records it against the account.
 *
 * Resolves to the key on success, or null if any step failed - the caller
 * keeps showing the local file either way, so a failed upload costs the
 * customer nothing visible now and simply does not survive the reinstall.
 * Throwing here would put an error sheet in front of somebody who has just
 * successfully changed their picture as far as they can tell.
 */
export async function uploadProfilePhoto(
  uri: string,
  contentType: string,
): Promise<string | null> {
  try {
    const ticket = await requestUploadUrl(contentType);

    /*
     * `fetch` rather than the app's own client: this goes to S3, not to our
     * API, and must carry no Authorization header - the presigned URL is the
     * credential, and an extra one makes S3 refuse the request.
     */
    const file = await fetch(uri);
    const body = await file.blob();

    const put = await fetch(ticket.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body,
    });
    if (!put.ok) return null;

    await recordPhoto(ticket.key);
    return ticket.key;
  } catch {
    return null;
  }
}

/** Forgets the picture on the account. The object itself is left alone. */
export async function clearProfilePhoto(): Promise<boolean> {
  try {
    await api.delete<{ cleared: boolean }>('/customers/me/photo');
    return true;
  } catch {
    return false;
  }
}
